import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { toast } from "sonner";
import { CheckCircle2, RefreshCw } from "lucide-react";

const inputCls = "w-full px-4 py-3 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "", confirm_password: "" });
  const [captcha, setCaptcha] = useState({ question: "", token: "" });
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [done, setDone] = useState(null); // verification url

  const loadCaptcha = () => api.get("/auth/captcha").then((r) => setCaptcha(r.data)).catch(() => {});
  useEffect(() => { loadCaptcha(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) { setError("Please agree to the Terms & Conditions and Privacy Policy to continue."); return; }
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, captcha_token: captcha.token, captcha_answer: parseInt(answer || "-1", 10) });
      setDone(data.dev_verification_url || true);
      toast.success("Account created!");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      loadCaptcha(); setAnswer("");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <AuthShell title="Almost there!" subtitle="We've created your account. Verify your email to start enrolling.">
        <div className="border border-[var(--line)] p-6 bg-white text-center" data-testid="register-success">
          <CheckCircle2 className="mx-auto text-[var(--accent)] mb-3" size={40} />
          <p className="text-sm text-[var(--ink-2)] mb-5">A verification link has been sent to <strong>{form.email}</strong>.</p>
          <div className="bg-[var(--alt)] p-3 text-xs text-[var(--ink-2)] mb-5">
            <strong>Development/testing only:</strong> live email is held until the sending domain is authenticated in Brevo. Use the link below to verify your account in the meantime.
          </div>
          {typeof done === "string" ? (
            <a href={done} data-testid="verify-now-link" className="block w-full bg-[var(--accent)] text-white font-semibold py-3 hover:bg-[var(--accent-hover)] transition-colors">Verify my email now</a>
          ) : (
            <Link to="/login" className="block w-full bg-[var(--accent)] text-white font-semibold py-3">Go to login</Link>
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create your account" subtitle="Get skilled in days, not years.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <input data-testid="reg-name" required placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
        <input data-testid="reg-email" type="email" required placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
        <input data-testid="reg-mobile" required placeholder="Mobile number" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className={inputCls} />
        <input data-testid="reg-password" type="password" required placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls} />
        <input data-testid="reg-confirm" type="password" required placeholder="Confirm password" value={form.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} className={inputCls} />
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="text-xs text-[var(--ink-2)] mb-1 block">Security check: {captcha.question || "…"}</label>
            <input data-testid="reg-captcha" required type="number" placeholder="Your answer" value={answer} onChange={(e) => setAnswer(e.target.value)} className={inputCls} />
          </div>
          <button type="button" onClick={loadCaptcha} className="mt-5 p-3 border border-[var(--line)] hover:border-[var(--accent)]" data-testid="reg-captcha-refresh"><RefreshCw size={16} /></button>
        </div>
        {error && <p className="text-sm text-[var(--destructive,#D50000)]" data-testid="register-error">{error}</p>}
        <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="reg-terms-label">
          <input type="checkbox" data-testid="reg-terms-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[var(--accent)] shrink-0" />
          <span className="text-xs text-[var(--ink-2)] leading-relaxed">I agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-[var(--ink)] hover:text-[var(--accent)] underline" data-testid="reg-terms-link">Terms &amp; Conditions</Link> and <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-semibold text-[var(--ink)] hover:text-[var(--accent)] underline" data-testid="reg-privacy-link">Privacy Policy</Link>.</span>
        </label>
        <button disabled={loading || !agreed} data-testid="register-submit" className="w-full bg-[var(--accent)] text-white font-semibold py-3.5 hover:bg-[var(--accent-hover)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed">{loading ? "Creating…" : "Create account"}</button>
      </form>
      <p className="mt-5 text-sm text-center text-[var(--ink-2)]">Already have an account? <Link to="/login" className="font-semibold text-[var(--ink)] hover:text-[var(--accent)]" data-testid="to-login">Log in</Link></p>
    </AuthShell>
  );
}
